import React, { useState } from 'react';
import type { VendorDto } from '../../services/api';

export type VendorFormData = {
  vendor_name: string;
  display_name: string;
  email: string;
  phone: string;
  tax_id: string;
  street_address: string;
  city: string;
  state_province: string;
  postal_code: string;
  country: string;
  payment_terms: string;
  status: string;
};

const initialForm: VendorFormData = {
  vendor_name: '',
  display_name: '',
  email: '',
  phone: '',
  tax_id: '',
  street_address: '',
  city: '',
  state_province: '',
  postal_code: '',
  country: '',
  payment_terms: '',
  status: 'Active',
};

type TabId = 'basic' | 'address' | 'terms';

interface AddVendorModalProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (data: VendorFormData) => Promise<void>;
  editVendor?: VendorDto | null;
}

export default function AddVendorModal({
  open,
  onClose,
  onSubmit,
  editVendor,
}: AddVendorModalProps) {
  const [activeTab, setActiveTab] = useState<TabId>('basic');
  const [form, setForm] = useState<VendorFormData>(() =>
    editVendor
      ? {
          vendor_name: editVendor.vendor_name,
          display_name: editVendor.display_name || '',
          email: editVendor.email || '',
          phone: editVendor.phone || '',
          tax_id: editVendor.tax_id || '',
          street_address: editVendor.street_address || '',
          city: editVendor.city || '',
          state_province: editVendor.state_province || '',
          postal_code: editVendor.postal_code || '',
          country: editVendor.country || '',
          payment_terms: editVendor.payment_terms || '',
          status: editVendor.status || 'Active',
        }
      : initialForm
  );
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});

  const isEdit = !!editVendor;
  const title = isEdit ? 'Edit Vendor' : 'Add New Vendor';
  const submitLabel = isEdit ? 'Save Vendor' : 'Create Vendor';

  const update = (field: keyof VendorFormData, value: string) => {
    setForm((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) setErrors((prev) => ({ ...prev, [field]: '' }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrors({});
    if (!form.vendor_name.trim()) {
      setErrors({ vendor_name: 'Vendor name is required.' });
      setActiveTab('basic');
      return;
    }
    setSaving(true);
    try {
      await onSubmit(form);
      setForm(initialForm);
      setActiveTab('basic');
      onClose();
    } catch (err: unknown) {
      const res = err as { errors?: Record<string, string[]> };
      if (res?.errors) {
        const flat: Record<string, string> = {};
        Object.entries(res.errors).forEach(([k, v]) => {
          if (Array.isArray(v) && v[0]) flat[k] = v[0];
        });
        setErrors(flat);
      }
    } finally {
      setSaving(false);
    }
  };

  const tabs: { id: TabId; label: string }[] = [
    { id: 'basic', label: 'Basic Info' },
    { id: 'address', label: 'Address' },
    { id: 'terms', label: 'Terms' },
  ];

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} aria-hidden="true" />
      <div
        className="relative w-full max-w-2xl max-h-[90vh] overflow-hidden flex flex-col bg-white rounded-2xl shadow-xl border border-gray-200"
        role="dialog"
        aria-modal="true"
        aria-labelledby="vendor-modal-title"
      >
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 shrink-0">
          <h2 id="vendor-modal-title" className="text-xl font-bold text-gray-900">
            {title}
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="p-2 rounded-lg text-gray-500 hover:bg-gray-100 hover:text-gray-700 transition-colors"
            aria-label="Close"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className="flex border-b border-gray-100 shrink-0">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              type="button"
              onClick={() => setActiveTab(tab.id)}
              className={`px-5 py-3 text-sm font-medium transition-colors ${
                activeTab === tab.id
                  ? 'text-gray-900 bg-white border-b-2 border-brand-500 text-brand-700 -mb-px'
                  : 'text-gray-500 hover:text-gray-700 bg-gray-50/80'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>

        <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto">
          <div className="p-6 space-y-4">
            {activeTab === 'basic' && (
              <>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Vendor Name <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      value={form.vendor_name}
                      onChange={(e) => update('vendor_name', e.target.value)}
                      placeholder="Supplier Co."
                      className={`w-full px-3 py-2 rounded-lg border text-gray-900 placeholder-gray-400 focus:ring-2 focus:ring-brand-500/20 focus:border-brand-500 outline-none ${
                        errors.vendor_name ? 'border-red-300' : 'border-gray-200'
                      }`}
                    />
                    {errors.vendor_name && (
                      <p className="mt-1 text-sm text-red-600">{errors.vendor_name}</p>
                    )}
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Display Name</label>
                    <input
                      type="text"
                      value={form.display_name}
                      onChange={(e) => update('display_name', e.target.value)}
                      placeholder="How they appear on bills"
                      className="w-full px-3 py-2 rounded-lg border border-gray-200 text-gray-900 placeholder-gray-400 focus:ring-2 focus:ring-brand-500/20 focus:border-brand-500 outline-none"
                    />
                  </div>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                    <input
                      type="email"
                      value={form.email}
                      onChange={(e) => update('email', e.target.value)}
                      placeholder="contact@supplier.com"
                      className="w-full px-3 py-2 rounded-lg border border-gray-200 text-gray-900 placeholder-gray-400 focus:ring-2 focus:ring-brand-500/20 focus:border-brand-500 outline-none"
                    />
                    {errors.email && <p className="mt-1 text-sm text-red-600">{errors.email}</p>}
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Phone</label>
                    <input
                      type="text"
                      value={form.phone}
                      onChange={(e) => update('phone', e.target.value)}
                      placeholder="+1 (555) 123-4567"
                      className="w-full px-3 py-2 rounded-lg border border-gray-200 text-gray-900 placeholder-gray-400 focus:ring-2 focus:ring-brand-500/20 focus:border-brand-500 outline-none"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Tax ID</label>
                  <input
                    type="text"
                    value={form.tax_id}
                    onChange={(e) => update('tax_id', e.target.value)}
                    placeholder="Tax identification number"
                    className="w-full px-3 py-2 rounded-lg border border-gray-200 text-gray-900 placeholder-gray-400 focus:ring-2 focus:ring-brand-500/20 focus:border-brand-500 outline-none"
                  />
                </div>
              </>
            )}

            {activeTab === 'address' && (
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Street Address</label>
                  <input
                    type="text"
                    value={form.street_address}
                    onChange={(e) => update('street_address', e.target.value)}
                    placeholder="123 Business Ave"
                    className="w-full px-3 py-2 rounded-lg border border-gray-200 text-gray-900 placeholder-gray-400 focus:ring-2 focus:ring-brand-500/20 focus:border-brand-500 outline-none"
                  />
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">City</label>
                    <input
                      type="text"
                      value={form.city}
                      onChange={(e) => update('city', e.target.value)}
                      placeholder="Manila"
                      className="w-full px-3 py-2 rounded-lg border border-gray-200 text-gray-900 placeholder-gray-400 focus:ring-2 focus:ring-brand-500/20 focus:border-brand-500 outline-none"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">State/Province</label>
                    <input
                      type="text"
                      value={form.state_province}
                      onChange={(e) => update('state_province', e.target.value)}
                      placeholder="NCR"
                      className="w-full px-3 py-2 rounded-lg border border-gray-200 text-gray-900 placeholder-gray-400 focus:ring-2 focus:ring-brand-500/20 focus:border-brand-500 outline-none"
                    />
                  </div>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Postal Code</label>
                    <input
                      type="text"
                      value={form.postal_code}
                      onChange={(e) => update('postal_code', e.target.value)}
                      placeholder="1000"
                      className="w-full px-3 py-2 rounded-lg border border-gray-200 text-gray-900 placeholder-gray-400 focus:ring-2 focus:ring-brand-500/20 focus:border-brand-500 outline-none"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Country</label>
                    <input
                      type="text"
                      value={form.country}
                      onChange={(e) => update('country', e.target.value)}
                      placeholder="Philippines"
                      className="w-full px-3 py-2 rounded-lg border border-gray-200 text-gray-900 placeholder-gray-400 focus:ring-2 focus:ring-brand-500/20 focus:border-brand-500 outline-none"
                    />
                  </div>
                </div>
              </div>
            )}

            {activeTab === 'terms' && (
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Payment Terms</label>
                  <input
                    type="text"
                    value={form.payment_terms}
                    onChange={(e) => update('payment_terms', e.target.value)}
                    placeholder="e.g. Net 30, Due on receipt"
                    className="w-full px-3 py-2 rounded-lg border border-gray-200 text-gray-900 placeholder-gray-400 focus:ring-2 focus:ring-brand-500/20 focus:border-brand-500 outline-none"
                  />
                </div>
                {isEdit && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
                    <select
                      value={form.status}
                      onChange={(e) => update('status', e.target.value)}
                      className="w-full px-3 py-2 rounded-lg border border-gray-200 text-gray-900 focus:ring-2 focus:ring-brand-500/20 focus:border-brand-500 outline-none"
                    >
                      <option value="Active">Active</option>
                      <option value="Inactive">Inactive</option>
                    </select>
                  </div>
                )}
              </div>
            )}
          </div>

          <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-gray-100 bg-gray-50/50 shrink-0">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2.5 text-sm font-medium text-gray-700 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving}
              className="px-5 py-2.5 text-sm font-semibold text-white bg-brand-600 rounded-lg hover:bg-brand-700 focus:ring-2 focus:ring-brand-500 focus:ring-offset-2 disabled:opacity-60 transition-colors"
            >
              {saving ? 'Saving...' : submitLabel}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
